/*
  PDP USP rows -> explainer panel.

  Progressive enhancement on purpose. The three rows are theme editor group
  blocks rendered as divs; restructuring the template to make them links would
  risk the layout of a block sitting directly under the buy button. Instead we
  find each row by its own copy, swap the inner wrapper to a <button> so the
  whole row is one real control with correct semantics, and open a shared
  panel. If this file fails to load the rows render exactly as before.

  Nothing here touches add to cart, the sticky CTA, or any cart behaviour.

  2026-09-14 visual pass: the panel became a right side drawer on desktop and
  a bottom sheet on mobile, and each row gained a chevron.

  2026-09-14 purchase-area pass: the guarantee block joins the same panel, by
  carrying an explicit data-uspd-trigger rather than being matched by text.
  It is our own markup, so there is nothing to guess. The three USP rows are
  still matched by copy and still filtered by containment, because that is
  the part that was hard to get right and it is proven on every product type.
*/
(function () {
  'use strict';

  /* One place for the panel headings, read by both binding paths. Declared
     before MATCHERS, which reads it at module init. */
  var TITLES = {
    payment: 'Payment options',
    shipping: 'Shipping & delivery',
    returns: 'Returns & guarantees',
    guarantee: '10-Game Guarantee'
  };

  var MATCHERS = [
    { key: 'payment',  title: TITLES.payment,  test: /buy now pay later/i },
    { key: 'shipping', title: TITLES.shipping, test: /free uk shipping/i },
    { key: 'returns',  title: TITLES.returns,  test: /30 day returns/i }
  ];

  var root, panel, titleEl, lastFocus, currentProductId;

  /* Same emitter contract as the Academy events: dataLayer only if one
     already exists, always a DOM event, never throws. */
  function track(name, detail) {
    detail = detail || {};
    detail.event = name;
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(detail);
      }
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) {}
  }

  function chevron() {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 6 10');
    svg.setAttribute('width', '6');
    svg.setAttribute('height', '10');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M1 1l4 4-4 4');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    svg.appendChild(path);
    var span = document.createElement('span');
    span.className = 'uspd-row__chev';
    span.appendChild(svg);
    return span;
  }

  function contentFor(key) {
    return root.querySelector('[data-uspd-content="' + key + '"]');
  }

  function open(key, title, productId) {
    if (!root) return;
    lastFocus = document.activeElement;

    Array.prototype.forEach.call(root.querySelectorAll('[data-uspd-content]'), function (el) {
      el.hidden = el.getAttribute('data-uspd-content') !== key;
    });
    if (titleEl) titleEl.textContent = title;

    var gutter = window.innerWidth - document.documentElement.clientWidth;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (gutter > 0) document.documentElement.style.paddingInlineEnd = gutter + 'px';
    if (panel) panel.focus();

    track('pdp_usp_open', { usp_type: key, product_id: productId || null });
  }

  function close() {
    if (!root || root.hidden) return;
    root.hidden = true;
    document.documentElement.style.overflow = '';
    document.documentElement.style.paddingInlineEnd = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  /* Keep tab focus inside the panel while it is open. */
  function trap(e) {
    if (e.key !== 'Tab' || root.hidden) return;
    var items = panel.querySelectorAll('a[href], button:not([disabled])');
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* Promote one element to a control. Both binding paths end up here, so a
     row and the guarantee card get the same semantics, chevron and handlers.
     Returns false if the element was already bound. */
  function bind(el, key, title, label, cls) {
    if (el.getAttribute('data-uspd-bound')) return false;
    el.setAttribute('data-uspd-bound', '1');
    el.classList.add(cls);
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('aria-haspopup', 'dialog');
    el.setAttribute('data-uspd-key', key);
    el.setAttribute('aria-label', label ? title + '. ' + label : title);
    el.appendChild(chevron());

    el.addEventListener('click', function () { open(key, title, currentProductId); });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        open(key, title, currentProductId);
      }
    });
    return true;
  }

  /*
    Keep the Swedish figure current for the people it is actually for.

    Sweden's zone rate is held in GBP and its market bills in SEK, so the
    number a Swedish shopper sees is an FX conversion Shopify makes at
    request time. It was verified against the live Swedish storefront's own
    shipping calculator on 2026-09-14, which returned 93.41 SEK, matching
    6.95 x the 13.440744 rate that market publishes. That rate moves.

    So in the SEK market the figure is recomputed from the rate that page
    itself publishes, using the same multiplication Shopify used. Everywhere
    else the verified figure stands. Wrapped in try/catch and gated on the
    currency, so a missing or renamed Shopify global changes nothing.
  */
  function refreshFx() {
    try {
      var cur = window.Shopify && window.Shopify.currency;
      if (!cur || cur.active !== 'SEK') return;
      var rate = parseFloat(cur.rate);
      if (!rate || rate <= 0) return;

      Array.prototype.forEach.call(
        document.querySelectorAll('[data-uspd-fx]'),
        function (el) {
          var gbp = parseFloat(el.getAttribute('data-uspd-fx'));
          if (!gbp) return;
          // The store's SEK format is "{{amount_with_space_separator}} kr".
          var v = (gbp * rate).toFixed(2).replace('.', ',');
          el.textContent = v + ' kr';
        }
      );
    } catch (e) {}
  }

  function enhance() {
    root = document.querySelector('[data-uspd]');
    if (!root) return;
    panel = root.querySelector('[data-uspd-panel]');
    titleEl = root.querySelector('[data-uspd-title]');

    var pEl = document.querySelector('[data-product-id]');
    if (pEl) currentProductId = pEl.getAttribute('data-product-id');

    /*
      Two passes. Collect candidates from the TEXT element, then drop any
      candidate that contains another candidate.

      Matching wrappers directly bound their ancestors too, because an
      ancestor's textContent contains its child's. Taking the label's nearest
      wrapper fixed most of it, but one label still resolved to a large
      container, so every row opened the same panel. Containment filtering is
      the rule that actually holds regardless of how the block is nested.
    */
    var candidates = [];
    var labels = document.querySelectorAll('p, span');
    Array.prototype.forEach.call(labels, function (label) {
      if (label.closest('[data-uspd]')) return;   // never enhance the panel itself
      /*
        ============================================================
        NEVER ENHANCE ANYTHING INSIDE THE CART DRAWER

        This scans every <p> and <span> on the page for USP copy. The cart
        drawer says "FREE UK SHIPPING" in three different places - the
        reward bar ("£40 AWAY FROM FREE UK SHIPPING"), the unlocked chip
        ("FREE UK SHIPPING UNLOCKED") and the upsell hook ("ADD & UNLOCK
        FREE UK SHIPPING") - so on any product page this claimed all three,
        added .uspd-row to their parents and bound a click handler to them.

        Two customer-visible faults, both reported from a real device:

          1. The reward copy stopped being green. .uspd-row p is one class
             plus an element, which outranks .hcd__upsell-hook, so the
             cart's earned-green lost to the USP row's body ink - but only
             on product pages, which is why it looked random.

          2. Tapping in the cart opened the Shipping & Delivery drawer. The
             handler was on the cart's own row, so a tap on the reward copy
             or anywhere in the upsell's text column opened a PDP panel the
             customer never asked for.

        The cart owns its own copy. This enhances the product page.
        ============================================================
      */
      if (label.closest('.hcd')) return;
      var text = (label.textContent || '').trim();
      if (!text || text.length > 60) return;      // a row label, not a container

      for (var i = 0; i < MATCHERS.length; i++) {
        var m = MATCHERS[i];
        if (!m.test.test(text)) continue;
        if (!contentFor(m.key)) return;
        var row = label.closest('.group-block-content') || label.parentElement;
        if (!row) return;
        candidates.push({ row: row, key: m.key, title: m.title, text: text });
        return;
      }
    });

    /* Keep only the innermost: a row that contains another candidate is a
       container, not a row. */
    var rows = candidates.filter(function (c) {
      return !candidates.some(function (other) {
        return other.row !== c.row && c.row.contains(other.row);
      });
    });

    rows.forEach(function (c) {
      if (!bind(c.row, c.key, c.title, c.text, 'uspd-row')) return;

      /*
        Market aware label, applied AFTER binding so the matcher above still
        sees the copy it was written against. A shopper outside the UK gets
        their own verified rate instead of a UK threshold that cannot apply to
        them. Liquid decides this from Shopify's market context; nothing is
        decided here.
      */
      if (c.key === 'shipping') {
        var swap = root.getAttribute('data-uspd-row-shipping');
        var label = swap ? c.row.querySelector('p') : null;
        if (label) {
          label.textContent = swap;
          c.row.setAttribute('aria-label', c.title + '. ' + swap);
        }
      }
    });

    /* Our own markup, so it declares its key rather than being recognised by
       its copy. Absent or ineligible products simply have no content block. */
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-uspd-trigger]'),
      function (el) {
        var key = el.getAttribute('data-uspd-trigger');
        if (!contentFor(key)) return;
        bind(el, key, TITLES[key] || '', el.textContent.trim().slice(0, 60), 'uspd-card');
      }
    );

    refreshFx();

    Array.prototype.forEach.call(root.querySelectorAll('[data-uspd-close]'), function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      trap(e);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }

  /* Variant swaps and app injections re-render the buy area, so re-bind when
     the block is replaced. Coalesced to one pass per frame. */
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; enhance(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
